import type { SfxId } from './sounds'

export type Wave = 'sine' | 'square' | 'tri' | 'saw' | 'noise'

/**
 * One voice of a placeholder sound: `wave` gliding from `f0` to `f1` Hz, starting `at` ms in,
 * lasting `ms`, peak gain `g`. (`noise` ignores the pitch.)
 */
export type Tone = readonly [wave: Wave, f0: number, f1: number, at: number, ms: number, g: number]

/**
 * Synthesized placeholder SFX (PHASE3 T3.6, D-051, O-24): short oscillator envelopes, each
 * readable on its own (rising = good for you, falling = bad, noise = impact). A real
 * `sfx_<id>.mp3` in `public/assets/audio/` replaces any of them by name.
 */
export const SYNTH: Readonly<Record<SfxId, readonly Tone[]>> = {
  click: [['square', 1800, 1200, 0, 25, 0.18]],
  jump: [['square', 330, 660, 0, 90, 0.22]],
  double_jump: [
    ['square', 500, 950, 0, 70, 0.2],
    ['tri', 950, 1500, 40, 70, 0.25],
  ],
  land: [
    ['noise', 0, 0, 0, 45, 0.18],
    ['sine', 150, 70, 0, 60, 0.35],
  ],
  pickup: [
    ['tri', 660, 660, 0, 60, 0.35],
    ['tri', 880, 880, 45, 60, 0.35],
    ['tri', 1320, 1320, 90, 80, 0.35],
  ],
  item_use: [['square', 520, 260, 0, 120, 0.22]],
  item_boost_jet: [
    ['noise', 0, 0, 0, 260, 0.25],
    ['saw', 200, 700, 0, 260, 0.18],
  ],
  item_shield_bubble: [
    ['sine', 400, 800, 0, 180, 0.35],
    ['sine', 800, 1250, 90, 160, 0.25],
  ],
  item_trip_wire: [
    ['tri', 950, 700, 0, 70, 0.35],
    ['tri', 950, 700, 80, 70, 0.3],
  ],
  item_spike_ball: [['square', 320, 140, 0, 150, 0.25]],
  item_homing_drone: [['saw', 700, 1100, 0, 220, 0.18]],
  item_oil_slick: [
    ['sine', 230, 90, 0, 220, 0.4],
    ['noise', 0, 0, 20, 120, 0.15],
  ],
  item_leader_missile: [
    ['saw', 300, 1300, 0, 320, 0.18],
    ['noise', 0, 0, 0, 320, 0.15],
  ],
  item_magnet: [
    ['sine', 300, 650, 0, 120, 0.35],
    ['sine', 650, 300, 120, 120, 0.3],
  ],
  hit: [
    ['noise', 0, 0, 0, 160, 0.4],
    ['square', 220, 70, 0, 210, 0.25],
  ],
  shield_pop: [
    ['tri', 1200, 300, 0, 120, 0.4],
    ['noise', 0, 0, 0, 40, 0.25],
  ],
  shard: [
    ['sine', 1320, 1320, 0, 50, 0.3],
    ['sine', 1760, 1760, 45, 90, 0.3],
  ],
  checkpoint: [
    ['tri', 784, 784, 0, 90, 0.35],
    ['tri', 1047, 1047, 80, 130, 0.35],
  ],
  respawn: [['sine', 300, 900, 0, 180, 0.35]],
  countdown_tick: [['square', 880, 880, 0, 90, 0.2]],
  go: [
    ['square', 1320, 1320, 0, 260, 0.18],
    ['tri', 660, 660, 0, 260, 0.3],
  ],
  finish: [
    ['tri', 523, 523, 0, 110, 0.35],
    ['tri', 659, 659, 100, 110, 0.35],
    ['tri', 784, 784, 200, 110, 0.35],
    ['tri', 1047, 1047, 300, 260, 0.35],
  ],
  podium: [
    ['tri', 392, 392, 0, 140, 0.3],
    ['tri', 523, 523, 130, 140, 0.3],
    ['tri', 659, 659, 260, 140, 0.3],
    ['tri', 784, 784, 390, 500, 0.3],
    ['sine', 392, 392, 390, 500, 0.2],
  ],
  reward_tick: [['sine', 1500, 1500, 0, 30, 0.25]],
  ride_summon: [
    ['saw', 150, 600, 0, 350, 0.18],
    ['tri', 300, 1200, 100, 250, 0.25],
  ],
  ride_end: [['tri', 600, 200, 0, 200, 0.3]],
}

/** Attack time; the rest of a tone decays quadratically to 0 (no clicks at either end). */
const ATTACK_MS = 3

/** Length of a sound in ms: where its last tone ends. */
export function synthLengthMs(tones: readonly Tone[]): number {
  return tones.reduce((m, t) => Math.max(m, t[3] + t[4]), 0)
}

function osc(wave: Wave, phase: number, noise: number): number {
  const p = phase - Math.floor(phase)
  switch (wave) {
    case 'sine':
      return Math.sin(2 * Math.PI * p)
    case 'square':
      return p < 0.5 ? 1 : -1
    case 'tri':
      return 1 - 4 * Math.abs(p - 0.5)
    case 'saw':
      return 2 * p - 1
    case 'noise':
      return noise
  }
}

/**
 * Mono samples of a placeholder sound at `sampleRate`, `synthLengthMs` long, clipped to ±1.
 * Pure (a fixed-seed noise generator), so tests render it without an `AudioContext`.
 */
export function renderSynth(tones: readonly Tone[], sampleRate: number): Float32Array {
  const out = new Float32Array(Math.round((synthLengthMs(tones) * sampleRate) / 1000))
  let seed = 0x2545f491
  for (const [wave, f0, f1, at, ms, g] of tones) {
    const start = Math.round((at * sampleRate) / 1000)
    const n = Math.round((ms * sampleRate) / 1000)
    const attack = Math.max(1, Math.round((ATTACK_MS * sampleRate) / 1000))
    let phase = 0
    for (let i = 0; i < n && start + i < out.length; i++) {
      const t = i / n
      seed = (Math.imul(seed, 1664525) + 1013904223) | 0
      const env = i < attack ? i / attack : (1 - t) * (1 - t)
      out[start + i]! += g * env * osc(wave, phase, seed / 0x80000000)
      phase += (f0 + (f1 - f0) * t) / sampleRate
    }
  }
  for (let i = 0; i < out.length; i++) out[i] = Math.max(-1, Math.min(1, out[i]!))
  return out
}
